import { api } from '@goaltree/backend/convex/_generated/api'
import { DEFAULT_GROVE_ICON } from '@goaltree/backend/convex/lib/groveIcons'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation } from 'convex/react'
import { Loader2 } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GroveIcon, GroveIconPicker } from '@/components/groves/grove-icon'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'

import {
  GROVE_DESCRIPTION_MAX,
  GROVE_NAME_MAX,
  groveErrorCode,
  toastGroveError,
} from './grove-utils'

const field =
  'w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/45 focus:outline-none'
const primaryBtn =
  'flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-[15px] font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50'

export type GroveFormValues = { name: string; icon: string; description: string }

export function GroveForm({
  initial,
  submitLabel,
  onSubmit,
  footer,
}: {
  initial?: GroveFormValues
  submitLabel: string
  onSubmit: (v: GroveFormValues) => Promise<void>
  footer?: ReactNode
}) {
  const { t } = useTranslation()
  const [name, setName] = useState(initial?.name ?? '')
  const [icon, setIcon] = useState(initial?.icon ?? DEFAULT_GROVE_ICON)
  const [description, setDescription] = useState(initial?.description ?? '')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || busy) return
    setBusy(true)
    try {
      await onSubmit({ name: name.trim(), icon, description: description.trim() })
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-keep-centered>
      <div className="flex items-center gap-3">
        <GroveIcon icon={icon} />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={GROVE_NAME_MAX}
          placeholder={t('groves.form.namePlaceholder')}
          aria-label={t('groves.form.name')}
          autoFocus
          className={field}
        />
      </div>
      <GroveIconPicker value={icon} onChange={setIcon} />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={GROVE_DESCRIPTION_MAX}
        rows={2}
        placeholder={t('groves.form.descriptionPlaceholder')}
        aria-label={t('groves.form.description')}
        className={cn(field, 'resize-none')}
      />
      <button type="submit" disabled={!name.trim() || busy} className={primaryBtn}>
        {busy && <Loader2 className="size-4 animate-spin" />}
        {submitLabel}
      </button>
      {footer}
    </form>
  )
}

export function CreateGroveDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useMutation(api.groves.create)
  const { tier, isLoading } = useIsPro()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const gated = open && !isLoading && tier === 'free'

  useEffect(() => {
    if (!gated) return
    onOpenChange(false)
    setUpgradeOpen(true)
  }, [gated, onOpenChange])

  return (
    <>
      <Dialog open={open && !gated} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('groves.create.title')}</DialogTitle>
          </DialogHeader>
          <p className="-mt-2 text-sm text-muted-foreground">{t('groves.create.desc')}</p>
          <GroveForm
            submitLabel={t('groves.create.submit')}
            onSubmit={async (v) => {
              try {
                const groveId = await create(v)
                onOpenChange(false)
                navigate({ to: '/groves/$groveId', params: { groveId } })
              } catch (err) {
                if (groveErrorCode(err) === 'proRequired') {
                  onOpenChange(false)
                  setUpgradeOpen(true)
                } else toastGroveError(err, t)
              }
            }}
          />
        </DialogContent>
      </Dialog>
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        reason="pro"
        title={t('groves.create.upgradeTitle')}
        description={t('groves.create.upgradeDesc')}
      />
    </>
  )
}

export function JoinCodeDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const join = useMutation(api.groves.join)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!code.trim() || busy) return
    setBusy(true)
    try {
      const groveId = await join({ code })
      onOpenChange(false)
      setCode('')
      navigate({ to: '/groves/$groveId', params: { groveId } })
    } catch (err) {
      toastGroveError(err, t)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('groves.joinCode.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4" data-keep-centered>
          <p className="-mt-2 text-sm text-muted-foreground">{t('groves.joinCode.desc')}</p>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('groves.joinCode.placeholder')}
            autoFocus
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            className={cn(field, 'font-mono tracking-widest')}
          />
          <button type="submit" disabled={!code.trim() || busy} className={primaryBtn}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            {t('groves.joinCode.submit')}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
